import { injectScript } from '@doveit/hammer';

export interface PlaceDetails {
  district: string,
  locality: string,
  plateCode: string,
  postalCode: string,
  latitude?: number,
  longitude?: number,
}

export interface AddressSuggestion {
  placeId: string,
  description: string,
}

const GMAP_API_ID = 'google-places-tag';
const GMAP_API_SCRIPT = (apiKey) => `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&language=it`;

function mapPlaceResult(placeResult: google.maps.places.PlaceResult): PlaceDetails {
  const gMapObject = placeResult.address_components.reduce<any>((result, item) => {
    result[item.types[0]] = item.short_name;
    return result;
  }, {});

  return {
    district: gMapObject.locality,
    locality: gMapObject.administrative_area_level_3 || gMapObject.locality,
    plateCode: gMapObject.administrative_area_level_2,
    postalCode: gMapObject.postal_code,
    latitude: placeResult.geometry?.location.lat(),
    longitude: placeResult.geometry?.location.lng(),
  };
}

export function getAddressSuggestions(
  apiKey: string,
  partialAddress: string,
  options: Partial<google.maps.places.AutocompletionRequest> = {},
): Promise<AddressSuggestion[]> {
  const script = GMAP_API_SCRIPT(apiKey);

  return injectScript(GMAP_API_ID, script, true, true)
    .then(() => {
      const { google } = window; // destructure after script has been loaded!!!
      const autocomplete = new google.maps.places.AutocompleteService();
      const { OK, ZERO_RESULTS } = google.maps.places.PlacesServiceStatus;

      return new Promise((resolve, reject) => {
        autocomplete.getPlacePredictions({
          ...options,
          input: partialAddress,
          componentRestrictions: { country: 'IT' },
          types: ['geocode'],
        }, (results, status) => {
          if (status !== OK && status !== ZERO_RESULTS) {
            const err = `google.maps places autocomplete failed with status: ${status}`;
            // eslint-disable-next-line no-console
            console.error(err);

            reject(new Error(err));
          }

          resolve(results ? results.map((result) => (
            {
              placeId: result.place_id,
              description: result.description,
            }
          )) : []);
        });
      });
    });
}

export function getAutoCompleteSessionToken(apiKey: string) {
  const script = GMAP_API_SCRIPT(apiKey);

  return injectScript(GMAP_API_ID, script)
    .then(() => {
      const { google } = window;
      return new google.maps.places.AutocompleteSessionToken();
    });
}

export function getPlaceDetails(
  apiKey: string,
  placeId: string,
  options: Partial<google.maps.places.PlaceDetailsRequest> = {},
): Promise<PlaceDetails> {
  const script = GMAP_API_SCRIPT(apiKey);

  if (!placeId) {
    return Promise.reject(new Error('placeId is mandatory'));
  }

  return injectScript(GMAP_API_ID, script)
    .then(() => {
      const { google, document } = window;

      const places = new google.maps.places.PlacesService(document.createElement('div'));
      const { OK } = google.maps.places.PlacesServiceStatus;

      return new Promise((resolve, reject) => {
        places.getDetails({
          ...options,
          placeId,
          fields: ['address_components', 'geometry'],
        }, (result, status) => {
          if (status !== OK) {
            const err = `google.maps get place data failed with status: ${status}`;
            // eslint-disable-next-line no-console
            console.error(err);

            reject(new Error(err));
          }

          return resolve(mapPlaceResult(result));
        });
      });
    });
}
